import React, {createContext, useState, useContext, useEffect} from "react";
import axiosInstance from "../config/axios-config";
import GlowingMessage from "../errors/glowing-message";
import {useTranslation} from 'react-i18next';
import i18n from "i18next";
import {eventBus} from "../event-bus";

const BasketContext = createContext();

export const useBasket = () => {
    const context = useContext(BasketContext);
    if (!context) {
        throw new Error("useBasket must be used within a BasketProvider");
    }
    return context;
};

export const BasketProvider = ({children}) => {
    const [basket, setBasket] = useState(0);
    const [message, setMessage] = useState("");
    const {t} = useTranslation();

    const addToBasket = async (product) => {
        try {
            const response = await axiosInstance.post("/api/v1/carts", product, {
                headers: {
                    "Content-Type": "application/json",
                    "Language-Context": i18n.language.toUpperCase() || "EN",
                },
            });
            if (response.status === 201) {
                setMessage(response.data.message)
                setBasket((prevBasket) => prevBasket + 1);
            }

            console.log(response)
            await getBasketCount();
        } catch (error) {
            const errorMessage = error.response?.data?.message || "Failed to add item to basket.";
            setMessage(errorMessage)
            setBasket((prevBasket) => Math.max(prevBasket - 1, 0)); // Revert on error
        }
    };

    const removeFromBasket = async (productId) => {
        try {
            await axiosInstance.delete(`/api/v1/carts/${productId}`);
            await getBasketCount();
        } catch (error) {
            const errorMessage = error.response?.data?.message || "Failed to remove item from basket.";
            setMessage(errorMessage)
        }
    };

    const buySelected = async (cartIds, partialApprovalPermissions = {}, expectedUnitPrices) => {
        try {
            await axiosInstance.post("/api/v1/orders/checkout", {
                cartIds, policyVersion: "quantity-reduction-v1", partialApprovalPermissions, expectedUnitPrices,
            }, {
                headers: {
                    "Content-Type": "application/json",
                },
            });
            await getBasketCount();
        } catch (error) {
            const errorMessage = error.response?.data?.message || "Failed to buy selected.";
            setMessage(errorMessage)
            throw error;
        }
    };

    const clearBasket = async (cartIds) => {
        try {
            await axiosInstance.delete("/api/v1/carts", {
                data: cartIds,
            });
            await getBasketCount();
        } catch (error) {
            const errorMessage = error.response?.data?.message || "Failed to clean basket.";
            setMessage(errorMessage)
            throw error;
        }
    };

    const deleteBasketItem = async (cartId) => {
        try {
            await axiosInstance.delete("/api/v1/carts", {
                data: [cartId]
            });
            await getBasketCount();
        } catch (error) {
            const errorMessage = error.response?.data?.message || "Failed to delete item from basket.";
            setMessage(errorMessage)
        }
    }

    const updateCountOfBasketItem = async (cartId, newQuantity) => {
        try {
            await axiosInstance.patch(`/api/v1/carts/${cartId}`, null, {
                params: {
                    quantity: newQuantity,
                },
            });
        } catch (error) {
            const errorMessage = error.response?.data?.message || "Failed to update count of basket item.";
            setMessage(errorMessage)
        }
    }

    const getBasketCount = async () => {
        try {
            const response = await axiosInstance.get("/api/v1/carts/count");
            setBasket(response.data); // Update state with the length
        } catch (error) {
            const errorMessage = error.response?.data?.message || "Failed to get count of items in basket.";
            setMessage(errorMessage)
        }
    };

    useEffect(() => {
        getBasketCount(); // Fetch initial count
    }, []);
    const totalItems = basket;

    const refreshBasketData = async () => {
        await getBasketCount()
        // Notify product tables to refresh via the event bus instead of a global function
        eventBus.emit("refreshProductsData")
    }

    return (
        <>
            <GlowingMessage message={message} onClose={() => setMessage("")}/>
            <BasketContext.Provider
                value={{
                    basket,
                    totalItems,
                    updateCountOfBasketItem,
                    getBasketCount,
                    addToBasket,
                    removeFromBasket,
                    buySelected,
                    clearBasket,
                    refreshBasketData,
                    deleteBasketItem,
                }}
            >
                {children}
            </BasketContext.Provider>
        </>
    );
};
